import { useEffect, useState } from "react";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Seo, { faqSchema } from "@/components/feature/Seo";
import kontaktHeroAsset from "@/assets/kontakt-hero-gebaeude.png";

const services = [
  "Photovoltaik & Speicher",
  "Wärmepumpe",
  "Wallbox & E-Mobilität",
  "Smart Home",
  "Notstrom & Ersatzstrom",
  "E-Check & Service",
  "Mieterstrom (G2)",
  "Sonstiges",
];

const faqs = [
  {
    q: "Welche Dienstleistungen bieten Sie an?",
    a: "Wir sind Ihr Elektro-Meisterbetrieb für Photovoltaik & Speicher, Wärmepumpen, Wallboxen & E-Mobilität, Smart Home, Notstrom- und Ersatzstromanlagen, klassische Elektroinstallation sowie E-Check und Service. Wir planen, bauen und betreuen Ihre Anlage komplett aus einer Hand.",
  },
  {
    q: "Wie kann ich einen Termin vereinbaren?",
    a: "Am schnellsten erreichen Sie uns telefonisch unter 02822 791010 zu unseren Telefonzeiten oder ganz bequem über das Kontaktformular auf dieser Seite. Wir melden uns innerhalb von 24 Stunden bei Ihnen und stimmen einen Termin vor Ort ab.",
  },
  {
    q: "Welche Leistungen bieten Sie für Privatkunden?",
    a: "Für Privatkunden bieten wir komplette Energie-Lösungen für Ein- und Mehrfamilienhäuser: Photovoltaik mit Stromspeicher, Wärmepumpe, Wallbox, Smart Home, Notstromversorgung sowie die gesamte Elektroinstallation – inklusive E-Check, Wartung und Service.",
  },
  {
    q: "Welche Leistungen bieten Sie für Gewerbekunden?",
    a: "Für Gewerbe und Industrie realisieren wir Photovoltaik-Anlagen auf großen Dachflächen, Lastmanagement und Ladeinfrastruktur für Fuhrparks, Energiemanagement-Systeme, Notstromlösungen sowie die komplette Elektrotechnik – planbar, normgerecht und mit langfristiger Betreuung.",
  },
  {
    q: "Wie engagieren Sie sich für den Umweltschutz?",
    a: "Nachhaltigkeit ist Teil unserer DNA: Wir bringen erneuerbare Energien direkt zu unseren Kunden, optimieren den Eigenverbrauch durch intelligente Speicher- und Energiemanagement-Systeme und setzen im eigenen Betrieb auf Elektromobilität und energieeffiziente Prozesse.",
  },
  {
    q: "Wie kann ich ein Angebot anfordern?",
    a: "Schicken Sie uns Ihre Anfrage einfach über das Formular auf dieser Seite – inklusive einer kurzen Projektbeschreibung und gerne mit Foto vom Objekt. Sie können uns auch telefonisch oder per E-Mail kontaktieren. Wir erstellen Ihnen ein transparentes, unverbindliches Angebot.",
  },
];

function FaqItem({ q, a, idx }: { q: string; a: string; idx: number }) {
  const [open, setOpen] = useState(false);
  const panelId = `faq-panel-${idx}`;
  return (
    <div className="border-b border-foreground-200">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-start justify-between gap-6 py-6 text-left group"
      >
        <span className="flex items-start gap-4 min-w-0">
          <span className="text-[#4CAF50] text-sm font-heading font-bold tabular-nums shrink-0 mt-1">
            {String(idx + 1).padStart(2, "0")}
          </span>
          <span className="font-heading font-bold uppercase text-foreground-950 text-base md:text-lg tracking-tight leading-tight">
            {q}
          </span>
        </span>
        <span
          aria-hidden="true"
          className={`shrink-0 w-10 h-10 rounded-full border border-foreground-300 flex items-center justify-center transition-all duration-300 ${
            open ? "bg-[#4CAF50] border-[#4CAF50] rotate-180" : "group-hover:border-foreground-900"
          }`}
        >
          <i
            className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-lg ${
              open ? "text-white" : "text-foreground-800"
            }`}
          />
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-hidden={!open}
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="pb-8 pl-10 pr-14 text-foreground-600 text-sm md:text-base leading-relaxed">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Kontakt() {
  useSmoothScroll();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    company: "",
    service: "",
    message: "",
    privacy: false,
  });
  const [fileName, setFileName] = useState<string>("");
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.privacy) return;
    setStatus("sending");
    try {
      const body = new URLSearchParams();
      body.append("name", formData.name);
      body.append("phone", formData.phone);
      body.append("email", formData.email);
      if (formData.company) body.append("company", formData.company);
      if (formData.service) body.append("service", formData.service);
      body.append("message", formData.message);
      const res = await fetch("https://readdy.ai/api/form/d8nhmaojb57qogjbhj90", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          company: "",
          service: "",
          message: "",
          privacy: false,
        });
        setFileName("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl bg-white border border-foreground-200 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-[#4CAF50] focus:ring-2 focus:ring-[#4CAF50]/20 transition-all";

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Kontakt | Zweering Elektrotechnik Emmerich am Rhein"
        description="Kontakt zu Zweering Elektrotechnik in Emmerich am Rhein — Elektro-Meisterbetrieb für Photovoltaik, Wärmepumpe, Wallbox und Smart Home. Senden Sie uns Ihre Anfrage."
        canonical="https://zweering.de/kontakt"
        breadcrumbs={[
          { name: "Startseite", url: "https://zweering.de/" },
          { name: "Kontakt", url: "https://zweering.de/kontakt" },
        ]}
        schema={faqSchema(faqs.map((f) => ({ q: f.q, a: f.a })))}
      />
      <Header />
      <main id="main-content">

        {/* Hero */}
        <section
          className="relative text-white pt-36 md:pt-44 pb-24 md:pb-32 px-6 md:px-10 lg:px-14 rounded-b-[40px] overflow-hidden"
          style={{
            backgroundImage: `url(${kontaktHeroAsset})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Dunkles Overlay für Lesbarkeit */}
          <div className="absolute inset-0 bg-black/55 pointer-events-none" />
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
               style={{
                 backgroundImage:
                   "radial-gradient(circle at 1px 1px, #4CAF50 1px, transparent 0)",
                 backgroundSize: "32px 32px",
               }}
          />
          <div className="relative max-w-7xl mx-auto">
            <p className="text-[#4CAF50] text-[11px] md:text-sm font-heading uppercase tracking-widest mb-5">
              Elektro-Meisterbetrieb · Emmerich am Rhein · ★ 5,0 Google
            </p>
            <h1 className="font-heading font-bold uppercase text-[30px] sm:text-[44px] md:text-[80px] lg:text-[112px] tracking-tight leading-[0.95]">
              Kontakt
            </h1>
            <p className="mt-6 md:mt-8 text-white/75 text-base md:text-lg max-w-2xl leading-relaxed">
              Senden Sie uns Ihre Anfrage — wir melden uns zeitnah bei Ihnen.
            </p>
          </div>
        </section>

        {/* Form + Info */}
        <section className="bg-white py-20 md:py-28 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-10 items-start">
            {/* Form */}
            <div className="bg-[#F4F4F4] rounded-3xl p-7 md:p-10">
              {status === "success" ? (
                <div>
                  <span className="inline-flex w-12 h-12 rounded-full bg-[#4CAF50] items-center justify-center mb-5">
                    <i className="ri-check-line text-white w-6 h-6 flex items-center justify-center text-xl" />
                  </span>
                  <h2 className="font-heading font-bold uppercase text-foreground-950 text-2xl md:text-3xl tracking-tight">
                    Vielen Dank!
                  </h2>
                  <p className="mt-3 text-foreground-600 leading-relaxed">
                    Ihre Anfrage ist bei uns eingegangen. Wir melden uns innerhalb von 24 Stunden bei Ihnen.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <h2 className="font-heading font-bold uppercase text-foreground-950 text-xl md:text-2xl tracking-tight mb-2">
                    Anfrage senden
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="k-name" className="block text-xs font-medium uppercase tracking-wider text-foreground-700 mb-1.5">
                        Name <span className="text-[#4CAF50]">*</span>
                      </label>
                      <input id="k-name" name="name" type="text" required value={formData.name} onChange={handleChange} placeholder="Ihr Name" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="k-phone" className="block text-xs font-medium uppercase tracking-wider text-foreground-700 mb-1.5">
                        Telefon <span className="text-[#4CAF50]">*</span>
                      </label>
                      <input id="k-phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange} placeholder="02822 791010" className={inputCls} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="k-email" className="block text-xs font-medium uppercase tracking-wider text-foreground-700 mb-1.5">
                        E-Mail <span className="text-[#4CAF50]">*</span>
                      </label>
                      <input id="k-email" name="email" type="email" required value={formData.email} onChange={handleChange} placeholder="ihre@email.de" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="k-company" className="block text-xs font-medium uppercase tracking-wider text-foreground-700 mb-1.5">
                        Firma <span className="text-foreground-400 normal-case font-normal">(optional)</span>
                      </label>
                      <input id="k-company" name="company" type="text" value={formData.company} onChange={handleChange} placeholder="Firmenname" className={inputCls} />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="k-service" className="block text-xs font-medium uppercase tracking-wider text-foreground-700 mb-1.5">
                      Welche Leistung interessiert Sie?
                    </label>
                    <select
                      id="k-service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className={`${inputCls} cursor-pointer pr-10 appearance-none`}
                      style={{
                        backgroundImage:
                          "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%230A0A0A' stroke-width='2'%3e%3cpath d='M6 9l6 6 6-6'/%3e%3c/svg%3e\")",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 14px center",
                        backgroundSize: "16px",
                      }}
                    >
                      <option value="">Bitte wählen ...</option>
                      {services.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="k-message" className="block text-xs font-medium uppercase tracking-wider text-foreground-700 mb-1.5">
                      Ihre Nachricht <span className="text-[#4CAF50]">*</span>
                    </label>
                    <textarea
                      id="k-message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Beschreiben Sie kurz Ihr Projekt – Standort, Wunschtermin, Besonderheiten ..."
                      className={`${inputCls} resize-none`}
                    />
                  </div>

                  <div>
                    <label htmlFor="k-file" className="block text-xs font-medium uppercase tracking-wider text-foreground-700 mb-1.5">
                      Optionaler Anhang <span className="text-foreground-400 normal-case font-normal">(Foto vor Ort oder Konkurrenzangebot)</span>
                    </label>
                    <label
                      htmlFor="k-file"
                      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-dashed border-foreground-300 hover:border-[#4CAF50] cursor-pointer transition-colors"
                    >
                      <span className="w-9 h-9 rounded-lg bg-[#F4F4F4] flex items-center justify-center shrink-0">
                        <i className="ri-attachment-line w-4 h-4 flex items-center justify-center text-foreground-700" />
                      </span>
                      <span className="text-sm text-foreground-600 truncate">
                        {fileName || "Datei auswählen ..."}
                      </span>
                    </label>
                    <input
                      id="k-file"
                      name="file"
                      type="file"
                      accept="image/*,application/pdf"
                      className="sr-only"
                      onChange={(e) => setFileName(e.target.files?.[0]?.name ?? "")}
                    />
                  </div>

                  <div className="flex items-start gap-3 pt-1">
                    <input
                      id="k-privacy"
                      type="checkbox"
                      required
                      checked={formData.privacy}
                      onChange={(e) => setFormData((p) => ({ ...p, privacy: e.target.checked }))}
                      className="mt-1 w-4 h-4 rounded accent-[#4CAF50] cursor-pointer"
                    />
                    <label htmlFor="k-privacy" className="text-foreground-600 text-sm leading-relaxed cursor-pointer">
                      Ich stimme der Datenverarbeitung zu.{" "}
                      <a href="/datenschutz" className="text-foreground-900 underline hover:text-[#4CAF50] transition-colors">
                        Datenschutzerklärung
                      </a>
                    </label>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#4CAF50] text-white font-body font-medium text-sm hover:bg-[#43A047] transition-colors disabled:opacity-60"
                    >
                      {status === "sending" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center" />
                          Wird gesendet ...
                        </>
                      ) : (
                        <>
                          Anfrage senden
                          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                        </>
                      )}
                    </button>
                    {status === "error" && (
                      <p className="text-red-600 text-sm mt-3">
                        Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns direkt an.
                      </p>
                    )}
                  </div>
                </form>
              )}
            </div>

            {/* Info column */}
            <div className="bg-[#0A0A0A] text-white rounded-3xl p-7 md:p-10 space-y-8">
              <h2 className="font-heading font-bold uppercase text-xl md:text-2xl tracking-tight">
                So erreichen Sie uns
              </h2>

              <a
                href="tel:+492822791010"
                className="group flex items-center justify-between gap-4 bg-[#4CAF50] hover:bg-[#43A047] transition-colors rounded-2xl px-5 py-5"
              >
                <span className="flex items-center gap-4 min-w-0">
                  <span className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                    <i className="ri-phone-fill w-5 h-5 flex items-center justify-center text-lg text-white" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-white/75 text-[11px] uppercase tracking-wider">Telefon</span>
                    <span className="block text-white font-heading font-bold text-lg md:text-xl tracking-tight">
                      02822 791010
                    </span>
                  </span>
                </span>
                <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center text-xl group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>

              <div className="space-y-1">
                <p className="text-white/40 text-[11px] uppercase tracking-wider font-medium mb-2">E-Mail</p>
                <a href="mailto:info@elektro-zweering.de" className="text-white hover:text-[#4CAF50] transition-colors text-base break-all">
                  info@elektro-zweering.de
                </a>
              </div>

              <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                <p className="text-[#4CAF50] text-[11px] uppercase tracking-wider font-heading mb-3">Telefonzeiten</p>
                <div className="space-y-1.5 text-white/85 text-sm">
                  <div className="flex justify-between gap-4">
                    <span>Mo – Do</span>
                    <span className="text-white">09:00 – 12:30 &amp; 14:00 – 16:30 Uhr</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span>Fr</span>
                    <span className="text-white">09:00 – 14:00 Uhr</span>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-white/40 text-[11px] uppercase tracking-wider font-medium mb-2">Adresse</p>
                <p className="text-white text-base leading-relaxed">
                  Zweering Elektrotechnik GmbH<br />
                  An der Schleuse 10<br />
                  46446 Emmerich am Rhein
                </p>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=An+der+Schleuse+10,+46446+Emmerich+am+Rhein"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Route zu An der Schleuse 10 in Google Maps öffnen"
                  className="group relative block mt-4 rounded-2xl overflow-hidden border border-white/10 h-40"
                >
                  <iframe
                    title="Standort Zweering Elektrotechnik – An der Schleuse 10, Emmerich am Rhein"
                    src="https://www.google.com/maps?q=An+der+Schleuse+10,+46446+Emmerich+am+Rhein&z=15&output=embed"
                    className="absolute inset-0 w-full h-full border-0 pointer-events-none grayscale-[20%] group-hover:grayscale-0 transition-all duration-500"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                  <span className="absolute right-3 bottom-3 w-9 h-9 rounded-full bg-[#4CAF50] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <i className="ri-arrow-right-up-line text-white w-4 h-4 flex items-center justify-center" />
                  </span>
                </a>
              </div>

              <div className="rounded-2xl border border-[#4CAF50] bg-[#4CAF50]/10 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-9 h-9 rounded-full bg-[#4CAF50] flex items-center justify-center shrink-0">
                    <i className="ri-flashlight-fill w-4 h-4 flex items-center justify-center text-white" />
                  </span>
                  <p className="font-heading font-bold uppercase text-white text-sm tracking-wider">
                    Strom aus? Wir kommen raus!
                  </p>
                </div>
                <p className="text-white/70 text-sm mb-4 leading-relaxed">
                  Unser Notdienst ist für Sie da, wenn es darauf ankommt.
                </p>
                <a
                  href="tel:+492822791010"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#0A0A0A] font-body font-medium text-sm hover:bg-[#4CAF50] hover:text-white transition-colors"
                >
                  <i className="ri-phone-fill w-4 h-4 flex items-center justify-center" />
                  Notdienst anrufen
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="bg-white px-6 md:px-10 lg:px-14 pb-20 md:pb-28">
          <div className="max-w-7xl mx-auto">
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=An+der+Schleuse+10,+46446+Emmerich+am+Rhein"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Route zu An der Schleuse 10, 46446 Emmerich am Rhein in Google Maps öffnen"
              className="group relative block rounded-3xl overflow-hidden bg-[#0A0A0A] h-[360px] md:h-[460px]"
            >
              <iframe
                title="Standort Zweering Elektrotechnik – An der Schleuse 10, Emmerich am Rhein"
                src="https://www.google.com/maps?q=An+der+Schleuse+10,+46446+Emmerich+am+Rhein&z=15&output=embed"
                className="absolute inset-0 w-full h-full border-0 pointer-events-none grayscale-[15%] group-hover:grayscale-0 transition-all duration-500"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-transparent pointer-events-none" />
              <div className="absolute left-6 right-6 bottom-6 flex items-center justify-between gap-3">
                <div className="text-white">
                  <p className="text-white/60 text-[11px] uppercase tracking-wider font-medium">Standort</p>
                  <p className="text-base font-heading font-bold uppercase tracking-tight">An der Schleuse 10, Emmerich</p>
                </div>
                <span className="w-12 h-12 rounded-full bg-[#4CAF50] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                  <i className="ri-arrow-right-up-line text-white w-5 h-5 flex items-center justify-center text-lg" />
                </span>
              </div>
            </a>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-[#F4F4F4] rounded-t-[40px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#4CAF50] text-xs md:text-sm font-heading uppercase tracking-widest mb-4">
              Häufige Fragen
            </p>
            <h2 className="font-heading font-bold uppercase text-[26px] sm:text-[36px] md:text-[52px] lg:text-[64px] text-foreground-950 tracking-tight leading-[0.95]">
              Antworten auf einen Blick
            </h2>
            <div className="mt-10 md:mt-14 border-t border-foreground-200">
              {faqs.map((f, i) => (
                <FaqItem key={f.q} q={f.q} a={f.a} idx={i} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}